import React from 'react'
import { IUser } from 'app/models/user'
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import isJwtValid, { getJwtCredentials, getJwtToken } from '../utils/jwt'
import { UserRepository } from '../data-access/users'
import { useAppContext } from 'app/app-context/app-context'
import { RoleRepository } from '../data-access/roles/role-repository'

export interface IUserContext {
  currentUser: IUser | null
  reloadCurrentUser: () => Promise<void>
  loadingCurrentUser: boolean
}

const UserContext = createContext<IUserContext>({} as IUserContext)
export const useUserContext = () => useContext(UserContext)
export const UserContextProvider: React.FC = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<IUser | null>(null)
  const [loadingCurrentUser, setLoadingCurrentUser] = useState(true)
  const { setRoles } = useAppContext()

  const reloadCurrentUser = useCallback(async () => {
    setLoadingCurrentUser(true)
    if (!isJwtValid()) {
      setCurrentUser(null)
      setLoadingCurrentUser(false)
      return
    }

    const token = getJwtToken()
    const jwtCredentials = getJwtCredentials(token)

    const userId = jwtCredentials.UserId

    const result = await UserRepository.getUser(userId)

    if (result.error || !result.data) {
      setCurrentUser(null)
      setLoadingCurrentUser(false)
      return
    }

    setCurrentUser(result.data)
    setLoadingCurrentUser(false)
  }, [])

  const getRoles = useCallback(async () => {
    if (!currentUser) {
      setRoles([])
      return
    }

    var result = await RoleRepository.getRoles()
    setRoles(result.data || [])
  }, [currentUser, setRoles])

  useEffect(() => {
    getRoles()
  }, [currentUser, getRoles])

  useEffect(() => {
    reloadCurrentUser()
  }, [reloadCurrentUser])

  return (
    <UserContext.Provider value={{ currentUser, reloadCurrentUser, loadingCurrentUser }}>
      {children}
    </UserContext.Provider>
  )
}
